import { ArrowRight, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { IMAGES, PCB_TYPES } from "@/data/site";

const FACILITY_PHOTOS = [
    { src: IMAGES.factoryDrill, caption: "Drilling / Routing Machine" },
    { src: IMAGES.factoryQuality, caption: "Quality Inspection" },
    { src: IMAGES.factoryTesting, caption: "Flying Probe & Functional Testing" },
    { src: IMAGES.factoryDispatch, caption: "Packing & Dispatch Area" },
];

export default function Gallery() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="PCB Gallery"
                eyebrow="Our PCBs & Facility"
                titleLines={["PCB", "Photo", "Gallery."]}
                lead="A look at the PCBs we manufacture and the facility behind them — grouped by board type."
            />

            <section className="bg-white py-20 md:py-28" data-testid="gallery-page">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>PCBs by Type</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Boards we manufacture
                        </h2>
                    </FadeUp>

                    <div className="mt-14 space-y-16 md:space-y-20" data-testid="gallery-groups">
                        {PCB_TYPES.map((t, i) => (
                            <FadeUp key={t.id} className={`grid items-center gap-8 md:grid-cols-2 md:gap-14 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`gallery-${t.id}`}>
                                <div className="relative">
                                    <img
                                        src={t.image}
                                        alt={`${t.name} — Unimicron Technology India`}
                                        className="h-[280px] w-full object-cover md:h-[360px]"
                                        data-testid={`gallery-photo-${t.id}`}
                                    />
                                    <div className="absolute left-0 top-0 bg-navy-dark px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                                        {t.tag}
                                    </div>
                                </div>
                                <div>
                                    <span className="font-mono text-[13px] tracking-[0.25em] text-brand-red">{t.num} /</span>
                                    <h3 className="mt-3 text-2xl font-bold tracking-tight text-navy md:text-3xl">{t.name}</h3>
                                    <p className="mt-4 text-[14.5px] leading-relaxed text-slate-600">{t.desc}</p>
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {t.applications.map((a) => (
                                            <span key={a} className="border border-slate-300 bg-white px-3 py-1 font-mono text-[10.5px] uppercase tracking-wider text-slate-600">
                                                {a}
                                            </span>
                                        ))}
                                    </div>
                                    <a
                                        href={`/pcb-manufacturing#${t.id}`}
                                        data-testid={`gallery-details-link-${t.id}`}
                                        className="group mt-6 inline-flex items-center gap-2 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:text-brand-red"
                                    >
                                        Details
                                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </a>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="gallery-facility">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Inside Our Facility</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Production, inspection, testing and dispatch
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="gallery-facility-grid">
                        {FACILITY_PHOTOS.map((p, i) => (
                            <FadeUp key={p.caption} delay={(i % 2) * 0.08} className="relative">
                                <img
                                    src={p.src}
                                    alt={`${p.caption} at Unimicron Technology India`}
                                    className="h-[260px] w-full object-cover md:h-[320px]"
                                    data-testid={`gallery-facility-photo-${i}`}
                                />
                                <p className="absolute bottom-0 left-0 bg-navy-dark px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white">
                                    {p.caption}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp className="mt-14 border border-slate-200 bg-white p-8 md:p-10">
                        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
                            <div>
                                <h3 className="text-xl font-bold tracking-tight text-navy">Need boards like these?</h3>
                                <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-slate-500">
                                    Send your PCB requirement and Gerber data — we will respond with pricing
                                    and delivery details.
                                </p>
                            </div>
                            <button
                                onClick={openQuote}
                                data-testid="gallery-cta-btn"
                                className="inline-flex shrink-0 items-center gap-2 border border-brand-red bg-brand-red px-7 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                            >
                                Get a Quote <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
